<template>
  <SlideIn
    :open="isOpen"
    heading="Add to Bag"
    :content-padding="false"
    @close="isOpen = false"
  >
    <template #content>
      <div v-if="activeProduct" class="quick-product">
        <div v-if="images.length" class="quick-product__carousel">
          <div
            v-for="image in images"
            :key="image.id"
            class="quick-product__image-wrapper"
          >
            <Media
              :images="image"
              :sources="image.sources"
              :preview-images="image.previewImages"
              cover
              eager
              no-transition
            />
          </div>
        </div>
        <div class="quick-product__content">
          {{ activeProduct.title }}
          <ProductCardPrice
            :product="activeProduct"
            class="quick-product__price"
            :class="{
              'quick-product__price--has-final-sale': shouldShowFinalSale,
            }"
          />
          <div v-if="shouldShowFinalSale" class="quick-product__final-sale">
            <button
              type="button"
              class="final-sale-trigger"
              @click="openFinalSaleModal"
            >
              <span class="final-sale-trigger__label">Final Sale</span>
              <img
                src="@/assets/images/question-mark.svg"
                alt=""
                class="question-mark-icon"
              />
            </button>
          </div>
          <div v-if="shipsFromUkWarehouse" class="quick-product__uk-warehouse">
            <button
              type="button"
              class="uk-warehouse-trigger"
              @click="openUkWarehouseModal"
            >
              <span class="uk-warehouse-trigger__label">UK Delivery</span>
              <img
                src="@/assets/images/question-mark.svg"
                alt=""
                class="question-mark-icon"
              />
            </button>
          </div>
          <PreorderDispatchLine
            v-if="showSellingPlanDescription"
            :dispatch-date="preorderDispatchDate"
            :fallback-text="preorderDispatchFallback"
          />
          <Variants
            v-if="showVariants && !quickLookIsPreLaunch"
            isolated
            hide-media
            class="quick-product__variants"
          />
          <div v-if="quickLookIsPreLaunch" class="quick-product__actions">
            <WishlistButton
              v-if="activeVariant"
              :product="activeProduct"
              :variant="activeVariant"
              combined
            />
          </div>
          <div v-else class="quick-product__actions">
            <div class="quick-product__add-row">
              <AddToBag isolated @clicked="setOpenQuickLook(false)" />
              <WishlistButton
                :product="activeProduct"
                :variant="activeVariant"
                inline
              />
            </div>
            <Button
              theme="secondary"
              tag="a"
              :href="activeProduct.onlineStoreUrl"
              width="full"
            >
              See Full details
            </Button>
          </div>
        </div>
      </div>
    </template>
  </SlideIn>
  <PreorderVariantModal />

  <SlideIn
    :open="finalSaleModalOpen"
    heading="Final Sale"
    @close="closeFinalSaleModal"
  >
    <template #content>
      <div class="final-sale-modal-content">
        <p>
          Final Sale Items: These are not eligible for return, except where
          required by law. For more details, please refer to our FAQs.
        </p>
      </div>
    </template>
  </SlideIn>

  <SlideIn
    :open="ukWarehouseModalOpen"
    heading="UK Delivery"
    @close="closeUkWarehouseModal"
  >
    <template #content>
      <div class="final-sale-modal-content">
        <p>{{ ukWarehouseDeliveryMessage }}</p>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import SlideIn from "@/components/SlideIn.vue";
import ProductCardPrice from "@/components/product-card/ProductCardPrice.vue";
import AddToBag from "@/components/product-display/AddToBagV3.vue";
import Variants from "@/components/product-display/Variants.vue";
import Button from "@/components/ButtonV3.vue";
import WishlistButton from "@/components/WishlistButtonV3.vue";
import { getMiniProductImages } from "@/helpers/miniProductImages.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import {
  getPreorderDispatchDate,
  getPreorderDispatchFallback,
} from "@/helpers/cartLineAttributes.js";
import Media from "@/components/Media.vue";
import PreorderVariantModal from "@/components/product-display/PreorderVariantModal.vue";
import PreorderDispatchLine from "@/components/product-display/PreorderDispatchLine.vue";
import { unwrapNodes } from "@/helpers/graphql";
import { hasFinalSaleTag } from "@/helpers/finalSale.js";
import {
  hasUkWarehouseTag,
  UK_WAREHOUSE_DELIVERY_MESSAGE,
} from "@/helpers/ukWarehouse.js";

export default {
  name: "MiniProduct",
  components: {
    SlideIn,
    ProductCardPrice,
    AddToBag,
    Variants,
    Button,
    WishlistButton,
    Media,
    PreorderVariantModal,
    PreorderDispatchLine,
  },
  data() {
    return {
      finalSaleModalOpen: false,
      ukWarehouseModalOpen: false,
    };
  },
  computed: {
    ...mapState("product", [
      "product",
      "openQuickLook",
      "quickLookProduct",
      "quickLookSelectedVariant",
    ]),
    ...mapGetters("product", ["variant", "isPreLaunch"]),
    activeProduct() {
      return this.quickLookProduct || this.product;
    },
    activeVariant() {
      if (this.quickLookProduct) {
        return this.quickLookSelectedVariant;
      }
      return this.variant;
    },
    quickLookIsPreLaunch() {
      return this.activeProduct?.tags?.includes("pre-launch") || false;
    },
    shipsFromUkWarehouse() {
      return hasUkWarehouseTag(this.activeProduct?.tags);
    },
    ukWarehouseDeliveryMessage() {
      return UK_WAREHOUSE_DELIVERY_MESSAGE;
    },
    shouldShowFinalSale() {
      const { finalSaleEnabled = false } = getFeatureToggles();
      if (!finalSaleEnabled) return false;

      return hasFinalSaleTag(this.activeProduct?.tags);
    },
    activeHidePreorder() {
      return this.activeProduct?.tags?.includes("hide-preorder") || false;
    },
    activePreOrder() {
      const preorder = this.activeVariant?.randbPreorder?.value;
      if (!preorder) return false;

      try {
        return JSON.parse(preorder);
      } catch {
        return false;
      }
    },
    activeAvailableForPreOrder() {
      if (this.activeHidePreorder || !this.activePreOrder) return false;

      const { enabled = false, limit = 0, reserved = 0 } = this.activePreOrder;
      const inventory = this.activeVariant?.quantityAvailable || 0;

      return enabled && inventory <= 0 && reserved < limit;
    },
    activeSelectedPrebuyCampaign() {
      if (!this.activeVariant?.prebuyCampaigns?.value) return null;

      let campaigns;
      try {
        campaigns = JSON.parse(this.activeVariant.prebuyCampaigns.value);
      } catch {
        return null;
      }

      const inventory = this.activeVariant?.quantityAvailable || 0;
      if (!campaigns || inventory <= 0) return null;

      const currentMarketId = window.shopifyMarket?.id;
      if (!currentMarketId) return null;

      return campaigns[String(currentMarketId)] || null;
    },
    activeAvailableForPrebuy() {
      if (this.activeHidePreorder) return false;

      return (
        !!this.activeSelectedPrebuyCampaign &&
        (this.activeVariant?.quantityAvailable || 0) > 0
      );
    },
    activeSellingPlanAllocation() {
      if (!this.activePreOrder?.sellingPlanId) return null;

      const sellingPlanAllocations =
        unwrapNodes(this.activeVariant?.sellingPlanAllocations?.edges) || [];

      return sellingPlanAllocations.find((allocation) => {
        return allocation.sellingPlan?.id === this.activePreOrder.sellingPlanId;
      });
    },
    activePrebuySellingPlanAllocation() {
      const campaign = this.activeSelectedPrebuyCampaign;
      if (!campaign?.sellingPlanId) return null;

      const sellingPlanAllocations =
        unwrapNodes(this.activeVariant?.sellingPlanAllocations?.edges) || [];
      const campaignPlanId = campaign.sellingPlanId.split("/").pop();

      return sellingPlanAllocations.find((allocation) => {
        const allocationPlanId = allocation.sellingPlan?.id.split("/").pop();
        return allocationPlanId === campaignPlanId;
      });
    },
    images() {
      return getMiniProductImages(this.activeProduct);
    },
    isOpen: {
      get() {
        return this.openQuickLook;
      },
      set(value) {
        return this.setOpenQuickLook(value);
      },
    },
    showVariants() {
      const variants = unwrapNodes(this.activeProduct?.variants?.edges) || [];
      return variants.length > 1;
    },
    preorderDispatchDate() {
      if (this.activeAvailableForPrebuy && this.activeSelectedPrebuyCampaign) {
        return getPreorderDispatchDate(
          this.activeSelectedPrebuyCampaign.estDispatchDate
        );
      }

      const description =
        this.activeSellingPlanAllocation?.sellingPlan?.description;
      const match = description?.match(/Expected Dispatch:\s*(.+)/i);

      return match ? match[1].trim() : null;
    },
    preorderDispatchFallback() {
      if (this.preorderDispatchDate) return null;

      if (this.activeAvailableForPrebuy && this.activeSelectedPrebuyCampaign) {
        return (
          this.activeSelectedPrebuyCampaign.productPageText ||
          getPreorderDispatchFallback(
            this.activePrebuySellingPlanAllocation?.sellingPlan?.description
          )
        );
      }

      return getPreorderDispatchFallback(
        this.activeSellingPlanAllocation?.sellingPlan?.description
      );
    },
    showSellingPlanDescription() {
      if (!this.activeAvailableForPreOrder && !this.activeAvailableForPrebuy) {
        return false;
      }

      return !!(this.preorderDispatchDate || this.preorderDispatchFallback);
    },
  },
  methods: {
    ...mapActions("product", ["setOpenQuickLook"]),
    openFinalSaleModal() {
      this.finalSaleModalOpen = true;
    },
    closeFinalSaleModal() {
      this.finalSaleModalOpen = false;
    },
    openUkWarehouseModal() {
      this.ukWarehouseModalOpen = true;
    },
    closeUkWarehouseModal() {
      this.ukWarehouseModalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.quick-product {
  padding: 20px 0;
}

.quick-product__carousel {
  display: flex;
  overflow-y: hidden;
  flex-flow: row nowrap;
  scroll-snap-type: x mandatory;
  gap: 4px;
  margin-bottom: 20px;
  padding-left: 20px;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    scroll-snap-align: start;
    scroll-snap-stop: always;
    flex: 0 0 240px;
    scroll-margin-left: 20px;
  }
}

.quick-product__image-wrapper {
  aspect-ratio: 4/5;

  // Give Media a definite height so its cover crop can resolve before the
  // image has an intrinsic size to measure
  > * {
    height: 100%;
  }
}

.quick-product__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  display: block;
}

.quick-product__content {
  padding: 0 20px;
}

.quick-product__variants {
  margin-bottom: 20px;
}

.quick-product__add-row {
  display: flex;
  width: 100%;

  :deep(.add-to-bag) {
    flex: 1;
  }
}

.quick-product__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quick-product__uk-warehouse {
  margin-bottom: 20px;

  .uk-warehouse-trigger {
    @include utility-label(14px);
    color: $color-black;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: normal;
    appearance: none;
    -webkit-appearance: none;

    &__label {
      display: inline-block;
      flex: 0 0 auto;
      color: inherit;
      white-space: nowrap;
    }

    &:hover {
      opacity: 0.8;
    }

    .question-mark-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }
}

.quick-product__price {
  margin-bottom: 20px;

  &--has-final-sale {
    margin-bottom: 4px;
  }
}

.quick-product__final-sale {
  margin-bottom: 20px;

  .final-sale-trigger {
    @include utility-label(14px);
    color: $color-red;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: normal;
    appearance: none;
    -webkit-appearance: none;

    &__label {
      display: inline-block;
      flex: 0 0 auto;
      color: inherit;
      white-space: nowrap;
    }

    &:hover {
      opacity: 0.8;
    }

    .question-mark-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }
}

.final-sale-modal-content {
  text-align: left;

  p {
    @include utility-body(16px);
    color: $color-black;
    line-height: 1.5;
    margin: 0;
  }
}
</style>
